import { encodeQR, QR_SIZE } from '../qr';

// PART 5: >= 4 modules of light "quiet zone" margin is an ISO/IEC 18004
// hard requirement, not a cosmetic choice -- most real scanners refuse to
// even attempt a decode without it (the finder-pattern search relies on a
// clean light border to anchor on).
const QUIET_ZONE = 4;

export interface QrCodeProps {
  /** The exact text to encode (e.g. the share link). */
  value: string;
  /** Accessible name for the image -- REQUIRED, not optional: an unlabeled
   * QR image is worse than none (a screen-reader user gets nothing to even
   * know it's there, let alone what it's for). */
  label: string;
  className?: string;
}

/** Inline SVG QR code, rendered from the from-scratch encoder in
 * game/../qr.ts (see that file's top comment for how its correctness is
 * verified against an independent reference). Renders `null` (not a
 * broken/truncated code) if `value` doesn't fit this app's fixed Version
 * 4/EC-M configuration -- the caller (ShareRoom.tsx) keeps every other
 * sharing affordance (code, copy, native share) working regardless, so a
 * pathologically long share link degrades gracefully instead of breaking
 * the whole panel. */
export function QrCode({ value, label, className }: QrCodeProps): JSX.Element | null {
  const matrix = encodeQR(value);
  if (!matrix) return null;

  const dim = QR_SIZE + QUIET_ZONE * 2;
  const modules: JSX.Element[] = [];
  for (let r = 0; r < QR_SIZE; r++) {
    for (let c = 0; c < QR_SIZE; c++) {
      if (matrix[r]![c]) {
        modules.push(<rect key={`${r}-${c}`} x={c + QUIET_ZONE} y={r + QUIET_ZONE} width={1} height={1} />);
      }
    }
  }

  return (
    <div className="qr-chip">
      <svg
        className={className ? `qr-chip-svg ${className}` : 'qr-chip-svg'}
        viewBox={`0 0 ${dim} ${dim}`}
        role="img"
        aria-label={label}
        shapeRendering="crispEdges"
        data-testid="qr-code"
      >
        {/* Fixed black-on-white (not theme-color) -- a QR scanner needs
            real, maximum contrast to reliably lock onto the finder
            patterns from a phone camera at an angle/distance; trading that
            for palette consistency with the app's dark theme would make
            the code less likely to actually scan, which defeats the whole
            feature. The surrounding `.qr-chip` (index.css) is what
            integrates with the dark UI instead. */}
        <rect x={0} y={0} width={dim} height={dim} fill="#ffffff" />
        <g fill="#000000">{modules}</g>
      </svg>
    </div>
  );
}
